import { useCallback, useState } from 'react'
import type { AccommodationMobileDayCellSelectHandler } from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendar.types'
import { diffInDays } from 'lib/datetime/dateUtils'
import { useAvailabilityForDate } from './useAvailabilityForDate'

interface UseAlternativeDateSelectionOptions {
  startDate: string | undefined
  endDate: string | undefined
  offerId: string | undefined
  rooms: Array<App.Occupants>
  lereGroup?: string
  onDateRangeChange: (startDate: string, endDate: string | undefined) => void
}

/**
 * Shared handler for alternative-dates day selection (used by both desktop
 * dropdown and mobile sidebar calendars). Manages the two-tap check-in →
 * check-out flow and keeps calendar state buffered locally.
 * Consumer provides onDateRangeChange to update local state.
 *
 * Availability fetch is triggered on completed range selection so calendar
 * availability can update without committing global offer-page dates.
 */
export function useAlternativeDateSelection({
  startDate,
  endDate,
  offerId,
  rooms,
  lereGroup,
  onDateRangeChange,
}: UseAlternativeDateSelectionOptions): AccommodationMobileDayCellSelectHandler {
  const [availabilityRequest, setAvailabilityRequest] = useState<{ checkInDate?: string, duration?: number }>({})

  useAvailabilityForDate(
    offerId,
    rooms,
    availabilityRequest.checkInDate,
    availabilityRequest.duration,
    lereGroup,
  )

  return useCallback((day) => {
    if (!startDate || endDate) {
      onDateRangeChange(day.date, undefined)
      return
    }

    const nights = diffInDays(new Date(startDate), new Date(day.date))
    if (nights <= 0) {
      onDateRangeChange(day.date, undefined)
      return
    }

    setAvailabilityRequest({ checkInDate: startDate, duration: nights })
    onDateRangeChange(startDate, day.date)
  }, [startDate, endDate, onDateRangeChange])
}
